/**
 * Pure inventory-operation logic (CC-6: NEVER use lingui macros here).
 *
 * An operation (stock_picking) groups lines (stock_move_line) and, when
 * confirmed (draft→done), materializes one stock_move per line that
 * updates the derived on-hand (stock_quant). The movement direction depends
 * on the operation type:
 *   - receipt  (receipt)      : ADDS at the destination location.
 *   - delivery (delivery)     : SUBTRACTS from the source location.
 *   - internal (transfer)     : SUBTRACTS from the source and ADDS at the destination.
 *
 * Quantities are in minor units (integers, factor 1000), same as F1.
 */

export type PickingKind = "receipt" | "delivery" | "internal";
export type PickingState = "draft" | "done";

/** Effect of a move on a quant: +delta at a given location. */
export interface QuantDelta {
  locationId: string;
  delta: number;
}

/** Composite-key separator (productId + locationId). */
const KEY_SEP = "::";

/** Valid operation types (for validation/selectors). */
export const PICKING_KINDS: readonly PickingKind[] = ["receipt", "delivery", "internal"] as const;

export function isPickingKind(value: string): value is PickingKind {
  return (PICKING_KINDS as readonly string[]).includes(value);
}

/** Normalizes a (possibly null) string to a valid PickingKind. */
export function coercePickingKind(value: string | null | undefined): PickingKind {
  return value && isPickingKind(value) ? value : "receipt";
}

/**
 * Resolves a line's source/destination according to the operation type and the
 * document's default locations. A receipt needs no source; a
 * delivery needs no destination; a transfer needs both.
 */
export function resolveLineLocations(
  kind: PickingKind,
  pickingSrc: string | null,
  pickingDst: string | null,
): { srcLocationId: string | null; dstLocationId: string | null } {
  if (kind === "receipt") {
    return { srcLocationId: null, dstLocationId: pickingDst };
  }
  if (kind === "delivery") {
    return { srcLocationId: pickingSrc, dstLocationId: null };
  }
  // internal
  return { srcLocationId: pickingSrc, dstLocationId: pickingDst };
}

/**
 * Computes the quant deltas produced by confirming an operation line.
 * Returns one or two deltas (transfer = outbound + inbound). Quantities <= 0
 * or missing locations produce an empty list (invalid line, ignored).
 */
export function computeLineQuantDeltas(
  kind: PickingKind,
  qty: number,
  srcLocationId: string | null,
  dstLocationId: string | null,
): QuantDelta[] {
  if (qty <= 0) {
    return [];
  }
  const deltas: QuantDelta[] = [];
  if (kind === "receipt") {
    if (dstLocationId) {
      deltas.push({ locationId: dstLocationId, delta: qty });
    }
  } else if (kind === "delivery") {
    if (srcLocationId) {
      deltas.push({ locationId: srcLocationId, delta: -qty });
    }
  } else {
    // internal: moves between locations (subtract from source, add to destination).
    if (srcLocationId && dstLocationId) {
      deltas.push({ locationId: srcLocationId, delta: -qty });
      deltas.push({ locationId: dstLocationId, delta: qty });
    }
  }
  return deltas;
}

/**
 * Deterministic composite key for (product, location[, lot]). The lot is
 * optional (F4): no lot is treated as an empty string, so untracked on-hand
 * never mixes with that of a specific lot.
 */
export function quantKey(
  productId: string,
  locationId: string,
  lotId: string | null = null,
): string {
  return `${productId}${KEY_SEP}${locationId}${KEY_SEP}${lotId ?? ""}`;
}

/** Effect of a line on on-hand (product×location×lot). */
export interface QuantEffect {
  productId: string;
  locationId: string;
  delta: number;
  /** Affected lot (F4). null = untracked. */
  lotId?: string | null;
}

/**
 * Aggregates the deltas of multiple lines by (product, location, lot). Useful for
 * applying all moves from a picking in a single pass per quant.
 */
export function aggregateQuantDeltas(lines: QuantEffect[]): Map<string, Required<QuantEffect>> {
  const map = new Map<string, Required<QuantEffect>>();
  for (const line of lines) {
    const lotId = line.lotId ?? null;
    const key = quantKey(line.productId, line.locationId, lotId);
    const existing = map.get(key);
    if (existing) {
      existing.delta += line.delta;
    } else {
      map.set(key, { ...line, lotId });
    }
  }
  return map;
}
